import { Injectable, PLATFORM_ID, WritableSignal, computed, effect, inject, signal, untracked } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, combineLatest, defer, forkJoin, of } from 'rxjs';
import {
  debounceTime,
  filter,
  map,
  startWith,
  switchMap,
  shareReplay,
  catchError,
} from 'rxjs/operators';
import { groupBy } from 'lodash-es';
import { ConnectwiseService } from './connectwise.service';
import { Company } from 'connectwise-rest/dist/Manage/CompanyAPI';
import { ChargeCode, TimeEntry, WorkRole, WorkType } from 'connectwise-rest/dist/Manage/TimeAPI';
import { Agreement } from 'connectwise-rest/dist/Manage/FinanceAPI';
import { ProjectTicket } from 'connectwise-rest/dist/Manage/ProjectAPI';
import { Ticket } from 'connectwise-rest/dist/Manage/ServiceAPI';
import { addMinutes, differenceInMinutes, isSameDay, isWithinInterval, parseISO, startOfDay, endOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { parse } from 'marked';
import { atLeastOneChargeTargetValidator } from '../../event-form/at-least-one-charge-target-validator';
import { EventInput } from 'fullcalendar';
import { DateService } from './date.service';

const LS_EVENTS_KEY = 'events';

export type BillableOption = NonNullable<TimeEntry['billableOption']>;

/**
 * CW has no endpoint listing these; they are a fixed enum in its schema. Keyed
 * by CW's own type, so the build fails if a value is added, renamed or dropped.
 */
const BILLABLE_OPTION_LABELS: Record<BillableOption, string> = {
  Billable: 'Billable',
  DoNotBill: 'Do Not Bill',
  NoCharge: 'No Charge',
  NoDefault: 'No Default',
};

export const BILLABLE_OPTIONS = (
  Object.entries(BILLABLE_OPTION_LABELS) as [BillableOption, string][]
).map(([value, label]) => ({ value, label }));

/**
 * Service to centralize form data filtering logic for time entry forms.
 * Provides reusable methods for filtering companies, agreements, tickets, etc.
 */
@Injectable({
  providedIn: 'root',
})
export class FormDataService {
  private readonly cwService = inject(ConnectwiseService);
  private readonly fb = inject(FormBuilder);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  readonly dateService = inject(DateService);

  constructor() {
    // Clear all cached form state whenever the user navigates to a different day
    // so stale FormGroups from the previous day never bleed into the new day's view.
    let initialized = false;
    effect(() => {
      this.dateService.currentDate(); // track
      if (initialized) {
        untracked(() => this.clearFormGroups());
      }
      initialized = true;
    });

    // Prune persisted events that fall outside the loaded timesheet date ranges.
    // This prevents stale events from accumulating in localStorage indefinitely.
    effect(() => {
      const timesheets = this.cwService.timesheetsResource.value();
      if (!timesheets?.length) return;

      const ranges = timesheets
        .filter((ts) => ts.dateStart && ts.dateEnd)
        .map((ts) => ({
          start: startOfDay(parseISO(ts.dateStart!)),
          end: endOfDay(parseISO(ts.dateEnd!)),
        }));

      if (!ranges.length) return;

      untracked(() => {
        const pruned = this.events().filter((e) => {
          if (!e.start) return false;
          const eventDate = e.start instanceof Date ? e.start : new Date(e.start as string);
          return ranges.some((range) =>
            isWithinInterval(eventDate, { start: range.start, end: range.end }),
          );
        });

        if (pruned.length !== this.events().length) {
          this.setEvents(pruned);
        }
      });
    });
  }

  // ─── Events (replaces CalendarState.events / CalendarState.currentEvents) ────

  /** All persisted events (current day and all days). */
  readonly events: WritableSignal<EventInput[]> = signal(this._loadEvents());

  /** Events filtered to the currently selected date. Replaces CalendarState.currentEvents. */
  readonly currentEvents = computed(() =>
    this.events().filter((e) => isSameDay(e.start as Date, this.dateService.currentDate())),
  );

  private _loadEvents(): EventInput[] {
    if (!isPlatformBrowser(this.platformId)) return [];
    try {
      const raw = localStorage.getItem(LS_EVENTS_KEY);
      if (!raw) {
        // Migrate from NGXS @@STATE
        const ngxsRaw = localStorage.getItem('@@STATE');
        if (ngxsRaw) {
          const parsed = JSON.parse(ngxsRaw);
          if (Array.isArray(parsed?.calendar?.events)) {
            return (parsed.calendar.events as any[]).map((e) => ({
              ...e,
              start: e.start ? new Date(e.start) : undefined,
              end: e.end ? new Date(e.end) : undefined,
            }));
          }
        }
        return [];
      }
      return (JSON.parse(raw) as any[]).map((e) => ({
        ...e,
        start: e.start ? new Date(e.start) : undefined,
        end: e.end ? new Date(e.end) : undefined,
      }));
    } catch {
      return [];
    }
  }

  setEvents(events: EventInput[]): void {
    this.events.set(events);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(LS_EVENTS_KEY, JSON.stringify(events));
    }
  }

  // Cached observables to prevent duplicate HTTP calls
  private companies$?: Observable<Company[]>;
  private chargeCodes$?: Observable<ChargeCode[]>;
  private workRoles$?: Observable<WorkRole[]>;
  private workTypes$?: Observable<WorkType[]>;
  private agreements$?: Observable<Agreement[]>;
  private serviceTickets$?: Observable<Ticket[]>;
  private projectTickets$?: Observable<ProjectTicket[]>;

  // Cached combined data for tree-select
  private allCombinedData$?: Observable<{
    serviceTickets: Ticket[];
    projectTickets: ProjectTicket[];
    chargeCodes: ChargeCode[];
  }>;

  /**
   * Cache of FormGroup instances keyed by FullCalendar event ID.
   * Sharing the same FormGroup object between EventDialogComponent and
   * ReviewComponent preserves the full FormControl state — values, validators,
   * touched/dirty flags, and any per-control errors — across navigation.
   */
  private readonly _formGroupCache = new Map<string, FormGroup>();

  getFormGroup(id: string): FormGroup | undefined {
    return this._formGroupCache.get(id);
  }

  setFormGroup(id: string, form: FormGroup): void {
    this._formGroupCache.set(id, form);
  }

  hasFormGroup(id: string): boolean {
    return this._formGroupCache.has(id);
  }

  /**
   * Removes an entry from the review list, the form cache and the persisted
   * events. An entry already in ConnectWise is deleted there first, so a failed
   * delete leaves it in place. `id` rather than `connectwiseId`: submitting sets
   * only `id`, and a partially failed submission keeps the user on the page.
   */
  deleteEntry(form: FormGroup): Observable<void> {
    const cwId = form.get('id')?.value as number | undefined;
    const remoteDelete: Observable<unknown> = cwId ? this.cwService.deleteTimeEntry(cwId) : of(null);
    return remoteDelete.pipe(
      map(() => {
        const index = this.items.controls.indexOf(form);
        if (index !== -1) this.items.removeAt(index);
        const timeEntryId = form.get('timeEntryId')?.value;
        if (timeEntryId) {
          this._formGroupCache.delete(timeEntryId);
          // Remove from the persisted events signal so it won't reappear on navigation
          this.setEvents(this.events().filter((e) => e.id !== timeEntryId));
        }
        if (cwId) this.cwService.reloadTimesheets();
      }),
    );
  }

  /** Drop all cached forms (call after a successful submission or a date change). */
  clearFormGroups(): void {
    this._formGroupCache.clear();
    this.items.clear();
  }

  /**
   * Sync the current FormArray values back into the events signal so that
   * changes made in ReviewComponent are visible in the calendar on navigation back.
   * Call this before navigating away from the review page.
   */
  syncFormsToEvents(): void {
    if (this.items.length === 0) return;

    // Update existing events from their matching forms
    const updated = this.events().map((event) => {
      const form = this.items.controls.find(
        (c) => c.get('timeEntryId')?.value === event.id,
      ) as FormGroup | undefined;

      if (!form) return event;

      const v = form.getRawValue();
      return {
        ...event,
        title: v.title ?? event.title,
        start: v.startDate ?? event.start,
        end:
          v.startDate != null && v.duration != null
            ? addMinutes(v.startDate as Date, (v.duration as number) * 60)
            : event.end,
        extendedProps: {
          ...event.extendedProps,
          // Persist the "has unsaved changes" bit. `submitted` is derived from
          // `updated` when the FormGroup is rebuilt, so without this a pending
          // edit would look already-submitted (and be skipped) after a reload.
          updated: event.extendedProps?.['updated'] || form.dirty,
          company: v.company,
          chargeCode: v.chargeToId,
          chargeToType: v.chargeToType,
          agreement: v.agreement,
          workType: v.workType,
          billableOption: v.billableOption,
          connectwiseId: v.connectwiseId,
        },
      };
    });

    // Also persist brand-new forms (added manually in ReviewComponent) that
    // have no matching entry in the events signal yet.
    const existingIds = new Set(this.events().map((e) => e.id).filter(Boolean));
    const newEvents: EventInput[] = this.items.controls
      .filter((c) => {
        const tid = c.get('timeEntryId')?.value;
        return !tid || !existingIds.has(tid);
      })
      .map((c) => {
        const v = (c as FormGroup).getRawValue();
        const start: Date = v.startDate ?? new Date();
        const end: Date = addMinutes(start, (v.duration as number) * 60);
        const id = `manual_${start.getTime()}_${Math.random().toString(36).slice(2)}`;
        return {
          id,
          title: v.title ?? '',
          start,
          end,
          color: '#4D86D2',
          extendedProps: {
            externalId: id,
            source: 'local',
            company: v.company,
            chargeCode: v.chargeToId,
            chargeToType: v.chargeToType,
            agreement: v.agreement,
            workType: v.workType,
            billableOption: v.billableOption,
          },
        } as EventInput;
      });

    this.setEvents([...updated, ...newEvents]);
  }

  // ─── Shared FormArray ─────────────────────────────────────────────────────

  /** The single source-of-truth form that both the dialog and review page bind to. */
  readonly submitForm = this.fb.group({ items: this.fb.array([]) });

  get items(): FormArray {
    return this.submitForm.get('items') as FormArray;
  }

  /**
   * Adds a calendar entry to the FormArray (if not already present) and
   * returns the backing FormGroup.  Safe to call multiple times for the same
   * event — subsequent calls only refresh the time-derived fields.
   */
  addEntry(entry: any): FormGroup {
    const eventId = entry.id as string | undefined;
    if (eventId) {
      const existing = this.items.controls.find(
        (c) => c.get('timeEntryId')?.value === eventId,
      ) as FormGroup | undefined;
      if (existing) {
        const newDuration =
          Math.round(
            (differenceInMinutes(entry.end as Date, entry.start as Date) / 60) * 4,
          ) / 4;
        const prevDuration = existing.get('duration')?.value;
        const prevStart = existing.get('startDate')?.value as Date | undefined;
        const timeMoved =
          newDuration !== prevDuration ||
          (prevStart instanceof Date && entry.start instanceof Date &&
            prevStart.getTime() !== (entry.start as Date).getTime());
        existing.get('duration')?.setValue(newDuration, { emitEvent: false });
        existing.get('startDate')?.setValue(entry.start, { emitEvent: false });
        if (timeMoved) existing.markAsDirty();
        if (!existing.get('title')?.dirty) {
          existing.get('title')?.setValue(entry.title, { emitEvent: false });
        }
        return existing;
      }
    }
    const form = this.buildOrGetFormGroup(entry);
    this.items.push(form);
    return form;
  }

  /** Returns the FormArray index of the entry matching the given event ID, or -1. */
  indexOfEntry(eventId: string): number {
    return this.items.controls.findIndex(
      (c) => c.get('timeEntryId')?.value === eventId,
    );
  }

  /**
   * Returns an existing cached FormGroup for the given event (refreshing
   * time-derived fields), or builds, caches, and returns a brand-new one.
   * All field mappings, markdown stripping, and chargeToId resolution live
   * here so neither EventDialogComponent nor ReviewComponent need to
   * duplicate them.
   */
  buildOrGetFormGroup(entry: any): FormGroup {
    const eventId = entry.id as string | undefined;
    const cached = eventId ? this._formGroupCache.get(eventId) : undefined;

    if (cached) {
      const newDuration =
        Math.round(
          (differenceInMinutes(entry.end as Date, entry.start as Date) / 60) * 4,
        ) / 4;
      const prevDuration = cached.get('duration')?.value;
      const prevStart = cached.get('startDate')?.value as Date | undefined;
      const timeMoved =
        newDuration !== prevDuration ||
        (prevStart instanceof Date && entry.start instanceof Date &&
          prevStart.getTime() !== (entry.start as Date).getTime());
      cached.get('duration')?.setValue(newDuration, { emitEvent: false });
      cached.get('startDate')?.setValue(entry.start, { emitEvent: false });
      if (timeMoved) cached.markAsDirty();
      // Sync calendar title only when the user hasn't manually edited it.
      if (!cached.get('title')?.dirty) {
        cached.get('title')?.setValue(entry.title, { emitEvent: false });
      }
      return cached;
    }

    const rawTitle = entry.title ?? '';

    const form = this.fb.group(
      {
        // ── Time-entry tracking ─────────────────────────────────────────────
        id: [entry.extendedProps?.['connectwiseId'] ?? undefined],
        timeEntryId: [eventId],
        externalId: [entry.extendedProps?.['externalId'] ?? null],
        details: [entry.extendedProps?.['details'] ?? []],
        workRole: [entry.extendedProps?.['workRole'] ?? undefined],
        // ── User-facing fields ──────────────────────────────────────────────
        title: [rawTitle, [Validators.required, Validators.minLength(4)]],
        company: [entry.extendedProps?.['company'] ?? null, Validators.required],
        chargeToId: [entry.extendedProps?.['chargeCode'] ?? undefined],
        chargeToType: [
          entry.extendedProps?.['chargeToType'] ??
          entry.extendedProps?.['chargeCode']?.type ??
          undefined,
        ],
        agreement: [entry.extendedProps?.['agreement'] ?? undefined],
        // Left undefined, CW applies the member/agreement defaults on create.
        workType: [entry.extendedProps?.['workType'] ?? undefined],
        billableOption: [entry.extendedProps?.['billableOption'] ?? undefined],
        duration: [
          Math.round(
            (differenceInMinutes(entry.end as Date, entry.start as Date) / 60) * 4,
          ) / 4,
          [Validators.required],
        ],
        startDate: [entry.start ?? new Date(), [Validators.required]],
        // ── Status flags ────────────────────────────────────────────────────
        hidden: [false],
        submitted: [
          !entry.extendedProps?.['updated'] && !!entry.extendedProps?.['connectwiseId'],
        ],
        type: [entry.extendedProps?.['type'] ?? 'event'],
        connectwiseId: [entry.extendedProps?.['connectwiseId'] ?? undefined],
      },
      { emitEvent: false }
    );

    // Ignore empty agreements
    if (entry.extendedProps?.['type'] !== 'cw') {
      form.addValidators(atLeastOneChargeTargetValidator());
      // `addValidators` registers without running. Without this the group-level
      // error stays absent until some unrelated child revalidates the group,
      // which both hides the error at load and reports the entry as valid.
      form.updateValueAndValidity({ emitEvent: false });
    }
    // Strip markdown from ConnectWise-sourced event titles.
    if (form.getRawValue().type === 'cw') {
      const plain = this.extractTextFromMarkdown(form.get('title')?.value);
      form.get('title')?.setValue(plain, { emitEvent: false, onlySelf: false });
    }

    // Resolve a bare numeric chargeToId to its full charge-code object. This
    // resolves asynchronously and can land after the event form has already
    // read the control, so let it emit — the form folds a late-arriving charge
    // target back into its select options.
    if (typeof entry.extendedProps?.['chargeToId'] === 'number') {
      this.getChargeTypeAndCodeById(entry.extendedProps['chargeToId']).subscribe((result) => {
        if (result.chargeCode && result.chargeType) {
          form.get('chargeToType')?.setValue(
            result.chargeCode.type || result.chargeType,
            { emitEvent: false },
          );
          form.get('chargeToId')?.setValue(result.chargeCode);
        }
      });
    }

    if (eventId) {
      this._formGroupCache.set(eventId, form);
    }

    return form;
  }

  private extractTextFromMarkdown(value: string | null | undefined): string {
    if (!value) return '';
    const html = parse(value) as string;
    const div = this.document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText;
  }

  /**
   * Get base data observables from ConnectWise service (cached)
   * These methods ensure HTTP calls are made only once and results are shared
   */
  getCompanies(): Observable<Company[]> {
    if (!this.companies$) {
      this.companies$ = this.cwService.getCompanies().pipe(
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.companies$;
  }

  getChargeCodes(): Observable<ChargeCode[]> {
    if (!this.chargeCodes$) {
      this.chargeCodes$ = this.cwService.getChargeCodes().pipe(
        // pin internal and project first
        map((codes) => {
          const PINNED_CHARGE_CODE_NAMES = ['Internal - General', 'Internal - Project']
              const rank = (code: ChargeCode): number => {
      const index = PINNED_CHARGE_CODE_NAMES.indexOf(code.name ?? '');
      return index === -1 ? PINNED_CHARGE_CODE_NAMES.length : index;
    };
    return [...codes].sort((a, b) => rank(a) - rank(b));
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.chargeCodes$;
  }

  getWorkRoles(): Observable<WorkRole[]> {
    if (!this.workRoles$) {
      this.workRoles$ = this.cwService.getWorkRoles().pipe(
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.workRoles$;
  }

  getWorkTypes(): Observable<WorkType[]> {
    if (!this.workTypes$) {
      this.workTypes$ = this.cwService.getWorkTypes().pipe(
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.workTypes$;
  }

  getAgreements(): Observable<Agreement[]> {
    if (!this.agreements$) {
      this.agreements$ = this.cwService.getAgreements().pipe(
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.agreements$;
  }

  getServiceTickets(): Observable<Ticket[]> {
    if (!this.serviceTickets$) {
      this.serviceTickets$ = this.cwService.getServiceTickets().pipe(
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.serviceTickets$;
  }

  getProjectTickets(): Observable<ProjectTicket[]> {
    if (!this.projectTickets$) {
      this.projectTickets$ = this.cwService.getProjectTickets().pipe(
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.projectTickets$;
  }

  /**
   * Filter companies based on form control value (user input)
   * @param companyControl - FormControl for company field
   * @returns Observable of filtered companies
   */
  getFilteredCustomers(companyControl: AbstractControl): Observable<Company[]> {
    return companyControl.valueChanges.pipe(
      startWith(''),
      filter((value) => typeof value === 'string'),
      debounceTime(200),
      switchMap((value) => {
        if (typeof value !== 'string') {
          return this.getCompanies();
        }
        return this.getCompanies().pipe(
          map((customers) => {
            return customers.filter(
              (customer) =>
                customer.name
                  .toLocaleLowerCase()
                  .includes(value.toLocaleLowerCase()) ||
                customer.identifier
                  .toLocaleLowerCase()
                  .includes(value.toLocaleLowerCase()),
            );
          }),
        );
      }),
    );
  }

  /**
   * Filter agreements based on selected company
   * @param companyControl - FormControl for company field
   * @param agreementControl - FormControl for agreement field (optional, for auto-select)
   * @returns Observable of filtered agreements
   */
  getFilteredAgreements(
    companyControl: AbstractControl,
    agreementControl?: AbstractControl,
  ): Observable<Agreement[]> {
    return defer(() =>
      companyControl.valueChanges.pipe(
        startWith(companyControl.value || undefined),
      ),
    ).pipe(
      switchMap((company) => {
        if (company && company.id !== undefined) {
          return this.getAgreements().pipe(
            map((agreements) => {
              return agreements.filter(
                (agreement) => agreement.company?.id === company.id,
              );
            }),
          );
        } else {
          return this.getAgreements();
        }
      }),
      map((agreements) => [{ name: 'No Agreement' }, ...agreements])
    );
  }

  /**
   * Filter service tickets based on selected company and user input
   * @param companyControl - FormControl for company field
   * @param chargeToIdControl - FormControl for chargeToId field (for filtering by input)
   * @returns Observable of filtered service tickets
   */
  getFilteredServiceTickets(
    companyControl: AbstractControl,
    chargeToIdControl: AbstractControl,
  ): Observable<Ticket[]> {
    return combineLatest({
      company: companyControl.valueChanges.pipe(
        startWith(companyControl.value || ''),
      ),
      value: chargeToIdControl.valueChanges.pipe(
        startWith(chargeToIdControl.value || ''),
      ),
    }).pipe(
      debounceTime(200),
      switchMap((res) => {
        return this.getServiceTickets().pipe(
          map((tickets) => {
            const filtered = res.company
              ? tickets.filter((t) => t.company?.id === res.company.id)
              : tickets;
            if (typeof res.value !== 'string') {
              return filtered;
            } else {
              return filtered.filter((t) =>
                JSON.stringify(t)
                  .toLocaleLowerCase()
                  .includes(res.value.toLocaleLowerCase()),
              );
            }
          }),
        );
      }),
    );
  }

  /**
   * Filter and group project tickets based on selected company and user input
   * @param companyControl - FormControl for company field
   * @param chargeToIdControl - FormControl for chargeToId field (for filtering by input)
   * @returns Observable of filtered and grouped project tickets
   */
  getFilteredProjectTickets(
    companyControl: AbstractControl,
    chargeToIdControl: AbstractControl,
  ) {
    return combineLatest({
      company: companyControl.valueChanges.pipe(
        startWith(companyControl.value || ''),
      ),
      value: chargeToIdControl.valueChanges.pipe(
        startWith(chargeToIdControl.value || ''),
      ),
    }).pipe(
      debounceTime(200),
      switchMap((res) => {
        return this.getProjectTickets().pipe(
          map((tickets) => {
            const filtered = res.company
              ? tickets.filter((t) => t.company?.id === res.company.id)
              : tickets;
            if (typeof res.value !== 'string') {
              return filtered;
            } else {
              return filtered.filter((t) =>
                JSON.stringify(t)
                  .toLocaleLowerCase()
                  .includes(res.value.toLocaleLowerCase()),
              );
            }
          }),
          map((tickets) => {
            return groupBy(tickets, 'project.name');
          }),
        );
      }),
    );
  }

  /**
   * Get combined charge code suggestions (service tickets + project tickets)
   * This can be used when you need a unified dropdown of all charge options
   * @param companyControl - FormControl for company field
   * @param chargeToIdControl - FormControl for chargeToId field
   * @returns Observable of combined grouped options
   */
  getCombinedChargeSuggestions(
    companyControl: AbstractControl,
    chargeToIdControl: AbstractControl,
  ): Observable<any[]> {
    return combineLatest({
      serviceTickets: this.getFilteredServiceTickets(
        companyControl,
        chargeToIdControl,
      ),
      projectTickets: this.getFilteredProjectTickets(
        companyControl,
        chargeToIdControl,
      ),
      chargeCodes: this.getChargeCodes(),
    }).pipe(
      map((res) => {
        const groups: any[] = [];

        // Add charge codes group
        if (res.chargeCodes.length > 0) {
          groups.push({
            label: 'Charge Codes',
            items: res.chargeCodes.map((cc) => ({
              ...cc,
              displayName: cc.name || cc.id?.toString(),
              type: 'ChargeCode',
            })),
          });
        }

        // Add service tickets group
        if (res.serviceTickets.length > 0) {
          groups.push({
            label: 'Service Tickets',
            items: res.serviceTickets.map((t) => ({
              ...t,
              displayName: `#${t.id} - ${t.summary}`,
              type: 'ServiceTicket',
            })),
          });
        }

        // Add project tickets groups (already grouped by project)
        Object.entries(res.projectTickets).forEach(([projectName, tickets]) => {
          groups.push({
            label: `Project: ${projectName}`,
            items: tickets.map((t) => ({
              ...t,
              displayName: `#${t.id} - ${t.summary}`,
              type: 'ProjectTicket',
            })),
          });
        });

        return groups;
      }),
    );
  }

  /**
   * Get cached combined data for all forms to share
   * This prevents duplicate HTTP calls when multiple forms are rendered
   */
  private getAllCombinedData(): Observable<{
    serviceTickets: Ticket[];
    projectTickets: ProjectTicket[];
    chargeCodes: ChargeCode[];
  }> {
    if (!this.allCombinedData$) {
      this.allCombinedData$ = combineLatest({
        serviceTickets: this.getServiceTickets(),
        projectTickets: this.getProjectTickets(),
        chargeCodes: this.getChargeCodes(),
      }).pipe(
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.allCombinedData$;
  }

  /**
   * Get combined charge code suggestions for tree-select (no filtering by input)
   * This version only watches company changes and doesn't create circular dependencies
   * @param companyControl - FormControl for company field
   * @returns Observable of combined grouped options
   */
  getCombinedChargeOptionsForTreeSelect(
    companyControl: AbstractControl,
  ): Observable<any[]> {
    return companyControl.valueChanges.pipe(
      startWith(companyControl.value || null),
      debounceTime(100),
      switchMap((company) => {
        // Use the shared cached data instead of calling services directly
        return this.getAllCombinedData().pipe(
          map((res) => {
            const groups: any[] = [];

            // Add charge codes group
            if (res.chargeCodes.length > 0) {
              groups.push({
                label: 'Charge Codes',
                items: res.chargeCodes.map((cc) => ({
                  ...cc,
                  displayName: cc.name || cc.id?.toString(),
                  type: 'ChargeCode',
                })),
              });
            }

            // Filter by company if selected
            const serviceTickets = company
              ? res.serviceTickets.filter((t) => t.company?.id === company.id)
              : res.serviceTickets;

            const projectTickets = company
              ? res.projectTickets.filter((t) => t.company?.id === company.id)
              : res.projectTickets;

            // Add service tickets group
            if (serviceTickets.length > 0) {
              groups.push({
                label: 'Service Tickets',
                items: serviceTickets.map((t) => ({
                  ...t,
                  displayName: `#${t.id} - ${t.summary}`,
                  type: 'ServiceTicket',
                })),
              });
            }

            // Add project tickets groups (group by project name)
            const groupedProjects = groupBy(projectTickets, 'project.name');
            Object.entries(groupedProjects).forEach(([projectName, tickets]) => {
              if (tickets.length > 0) {
                groups.push({
                  label: `Project: ${projectName}`,
                  items: tickets.map((t: any) => ({
                    ...t,
                    displayName: `#${t.id} - ${t.summary}`,
                    type: 'ProjectTicket',
                  })),
                });
              }
            });

            return groups;
          }),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * Determine the charge type and find the charge code object by ID
   * @param chargeToId The ID to search for
   * @returns Observable with the found charge code and its type
   */
  getChargeTypeAndCodeById(chargeToId: number): Observable<{
    chargeCode: any;
    chargeType: 'ChargeCode' | 'ServiceTicket' | 'ProjectTicket' | null;
  }> {
    return this.getAllCombinedData().pipe(
      map((result) => {
        const chargeToIdStr = chargeToId.toString();

        // Check if it's a ChargeCode
        let foundedChargeCode: any = result.chargeCodes.find(
          (chargeCode) => chargeCode.id?.toString() === chargeToIdStr
        );
        if (foundedChargeCode) {
          return {
            chargeCode: { ...foundedChargeCode, type: 'ChargeCode' },
            chargeType: 'ChargeCode' as const
          };
        }

        // Check if it's a ServiceTicket
        foundedChargeCode = result.serviceTickets.find(
          (ticket) => ticket.id?.toString() === chargeToIdStr
        );
        if (foundedChargeCode) {
          return {
            chargeCode: { ...foundedChargeCode, type: 'ServiceTicket' },
            chargeType: 'ServiceTicket' as const
          };
        }

        // Check if it's a ProjectTicket
        foundedChargeCode = result.projectTickets.find(
          (ticket) => ticket.id?.toString() === chargeToIdStr
        );
        if (foundedChargeCode) {
          return {
            chargeCode: { ...foundedChargeCode, type: 'ProjectTicket' },
            chargeType: 'ProjectTicket' as const
          };
        }

        // Not found in the cached lists.
        return { chargeCode: null, chargeType: null };
      }),
      switchMap((result) =>
        result.chargeCode ? of(result) : this.lookupTicketById(chargeToId),
      ),
    );
  }

  /** Memoized per-id lookups, so rebuilding a form doesn't refetch. */
  private readonly _ticketLookupCache = new Map<
    number,
    Observable<{
      chargeCode: any;
      chargeType: 'ChargeCode' | 'ServiceTicket' | 'ProjectTicket' | null;
    }>
  >();

  /**
   * Last-resort lookup for a charge target that isn't in any of the cached
   * lists — a ticket that isn't assigned to this user, most often one found
   * through CW search. Both endpoints resolve a numeric search directly by id.
   */
  private lookupTicketById(chargeToId: number): Observable<{
    chargeCode: any;
    chargeType: 'ChargeCode' | 'ServiceTicket' | 'ProjectTicket' | null;
  }> {
    const cached = this._ticketLookupCache.get(chargeToId);
    if (cached) return cached;

    const needle = chargeToId.toString();
    const lookup = forkJoin({
      serviceTickets: this.cwService
        .getServiceTickets(needle)
        .pipe(catchError(() => of([]))),
      projectTickets: this.cwService
        .getProjectTickets(needle)
        .pipe(catchError(() => of([]))),
    }).pipe(
      map(({ serviceTickets, projectTickets }) => {
        const serviceTicket = serviceTickets?.[0];
        if (serviceTicket) {
          return {
            chargeCode: this.asChargeTarget(serviceTicket, 'ServiceTicket'),
            chargeType: 'ServiceTicket' as const,
          };
        }

        const projectTicket = projectTickets?.[0];
        if (projectTicket) {
          return {
            chargeCode: this.asChargeTarget(projectTicket, 'ProjectTicket'),
            chargeType: 'ProjectTicket' as const,
          };
        }

        return { chargeCode: null, chargeType: null };
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this._ticketLookupCache.set(chargeToId, lookup);
    return lookup;
  }

  /**
   * Shapes a ticket the way the charge-code option lists do, so p-select can
   * render it even though it never appeared in those lists.
   */
  private asChargeTarget(
    ticket: any,
    type: 'ServiceTicket' | 'ProjectTicket',
  ): any {
    return { ...ticket, type, name: `#${ticket.id} - ${ticket.summary ?? ''}` };
  }

  /**
   * Determine the charge type for a given charge code object
   * @param chargeCode The charge code object to check
   * @returns Observable with the determined charge type
   */
  determineChargeType(chargeCode: any): Observable<'ChargeCode' | 'ServiceTicket' | 'ProjectTicket' | null> {
    if (!chargeCode || !chargeCode.id) {
      return new Observable(subscriber => {
        subscriber.next(null);
        subscriber.complete();
      });
    }

    return this.getAllCombinedData().pipe(
      map((result) => {
        // Check which collection contains this charge code
        if (result.chargeCodes.find((cc) => cc.id === chargeCode.id)) {
          return 'ChargeCode' as const;
        }
        if (result.serviceTickets.find((st) => st.id === chargeCode.id)) {
          return 'ServiceTicket' as const;
        }
        if (result.projectTickets.find((pt) => pt.id === chargeCode.id)) {
          return 'ProjectTicket' as const;
        }
        return null;
      })
    );
  }

  /**
   * Transform form data to TimeEntry format for ConnectWise submission
   * @param formValue The form value containing time entry data
   * @param username The username of the member submitting the entry
   * @returns TimeEntry object ready for ConnectWise API
   */
  formToTimeEntry(formValue: any, username: string): TimeEntry {
    const timeEntry: TimeEntry = {
      chargeToId: formValue.chargeToId?.id ?? undefined,
      chargeToType: formValue.chargeToType ?? undefined,
      member: {
        identifier: username.split('@')[0],
      },
      agreement: formValue.agreement?.id
        ? { id: formValue.agreement.id }
        : undefined,
      workType: formValue.workType?.id
        ? { id: formValue.workType.id }
        : undefined,
      billableOption: formValue.billableOption ?? undefined,
      notes: formValue.title,
      emailContactFlag: false,
      emailCcFlag: false,
      emailResourceFlag: false,
      timeStart: formatInTimeZone(
        formValue.startDate,
        'UTC',
        `yyyy-MM-dd'T'HH:mm:ssX`,
      ),
      timeEnd: formatInTimeZone(
        addMinutes(formValue.startDate, formValue.duration * 60),
        'UTC',
        `yyyy-MM-dd'T'HH:mm:ssX`,
      ),
    };

    // Add ID if updating existing entry
    if (formValue.id) {
      timeEntry.id = formValue.id;
    }

    // Add company if no direct chargeToId
    if (!formValue.chargeToId?.id && formValue.company?.id) {
      timeEntry.company = { id: formValue.company.id };
    }

    return timeEntry;
  }
}
